import React from "react";
import {
    IClinician,
    ServiceDeliveryOption,
    IAvailabilitySchedule,
} from "intake_shared/src/types";
import { Dialog } from "../../../bits/Dialog/Dialog";
import CustomDateSelector from "../../../bits/CustomDateSelector/CustomDateSelector";
import Alert from "../../../bits/Alert/Alert";
import { TextArea } from "../../../bits/TextArea/TextArea";
import moment from "../../../../../utils/app_moment";
import styles from "./RescheduleIntakeSessionDialog.module.scss";

interface RescheduleIntakeSessionDialogProps {
    open: boolean;
    onClose: () => void;
    loggedInClinician: IClinician;
    selectedSchedule: IAvailabilitySchedule | null;
    draftIntakeSession: {
        from: Date;
        to: Date;
        serviceDeliveryOption: ServiceDeliveryOption;
        practice_id: string;
    };
    setDraftIntakeSession: (data: {
        from: Date;
        to: Date;
        serviceDeliveryOption: ServiceDeliveryOption;
        practice_id: string;
    }) => void;
    reason: string;
    setReason: (reason: string) => void;
    onSave: () => void;
    isBooked?: boolean;
}

export default function RescheduleIntakeSessionDialog({
    open,
    onClose,
    loggedInClinician,
    selectedSchedule,
    draftIntakeSession,
    setDraftIntakeSession,
    reason,
    setReason,
    onSave,
    isBooked = false,
}: RescheduleIntakeSessionDialogProps) {
    const [showRescheduleEmailNoticeDialog, setShowRescheduleEmailNoticeDialog] =
        React.useState(false);

    React.useEffect(() => {
        if (!open) {
            setShowRescheduleEmailNoticeDialog(false);
        }
    }, [open]);

    const handleFinalConfirm = () => {
        onSave();
    };

    const handlePrimaryAction = () => {
        if (isBooked) {
            setShowRescheduleEmailNoticeDialog(true);
            return;
        }
        onSave();
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                title={
                    isBooked
                        ? "Reschedule Intake Session"
                        : "Reschedule Intake Session Availability"
                }
                onSave={handlePrimaryAction}
                onCancel={onClose}
                saveText={isBooked ? "Continue" : "Reschedule"}
                cancelText="Cancel"
                isSubmitBtnDisabled={!reason.trim()}
            >
                <div className="v2-form">
                    <Alert type="warning" className={styles.infoNote}>
                        Supervisor Intake sessions are one-off and remote by default
                    </Alert>
                    <CustomDateSelector
                        showEndTime={false}
                        onChange={(from, to, endDate) => {
                            const endTime = new Date(from);
                            endTime.setMinutes(endTime.getMinutes() + 45);

                            setDraftIntakeSession({
                                ...draftIntakeSession,
                                from,
                                to: endTime,
                            });
                        }}
                        defaultDate={
                            draftIntakeSession.from
                                ? {
                                      day: moment(draftIntakeSession.from).day(),
                                      fromTime: moment(
                                          draftIntakeSession.from
                                      ).format("HH:mm"),
                                      startingDate: moment(draftIntakeSession.from),
                                      endingDate: null,
                                  }
                                : undefined
                        }
                        departments={loggedInClinician.departments}
                    />
                    <div style={{ marginTop: 16 }}>
                        <TextArea
                            label="Reason for rescheduling"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="Enter reason for rescheduling"
                            required
                        />
                    </div>
                </div>
            </Dialog>
            <Dialog
                open={showRescheduleEmailNoticeDialog}
                onClose={() => setShowRescheduleEmailNoticeDialog(false)}
                title="Confirm Reschedule"
                onSave={() => {
                    setShowRescheduleEmailNoticeDialog(false);
                    handleFinalConfirm();
                }}
                onCancel={() => setShowRescheduleEmailNoticeDialog(false)}
                saveText="Confirm"
                cancelText="Cancel"
            >
                Since this session is being rescheduled, we will send a new email
                with updated details to both the client and clinician. Are you sure
                you want to reschedule this session?
            </Dialog>
        </>
    );
}
